import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    SimpleGrid,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Button,
    chakra,
    useColorModeValue,
    Input, Stack, InputLeftElement, InputGroup
} from '@chakra-ui/react';
import { useEvoContext } from "../../context/evo.context";
import { Footer } from "../footer/Footer";
import { Header } from "../header/Header";
import {ReadIssueLink, ReadIssueWithImageLink} from "../links/ReadIssueWithImageLink";
import IssuesDataProvider from "../dataProviderEvo/issues-data-provider";
import { ROUTE_MAIN, ROUTE_PURCHASE } from "../../App";
import {FormControlEvo} from "../forms/chakra/FormControlEvo";
import {FormLabelEvo} from "../forms/chakra/label/FormLabelEvo";
import {fieldNameAndLabels} from "../../infrastructure/utils/formUtils";
import {SearchIcon} from "@chakra-ui/icons";
import PublicIssuesDataProvider from "../dataProviderEvo/public-issues-data-provider";

interface Issue {
    id_issue: string;
    title: string;
    header: string;
    year: string;
    date_publish: {
        date: string;
        timezone_type: number;
        timezone: string;
    };
    image: {
      image_url: string;
    }
    pdf: string | null;
    publicCount: number;
    state: {
        code: string;
        value: string;
        page_count: string;
    };
}

type Params = {
    fulltext?: string;
};

export function JournalListTabs() {
    const {authToken } = useEvoContext();
    const [issueData, setIssueData] = useState<Record<string, Issue[]>>({});
    const [searchString, setSearchString] = useState<string>('');
    const [doSearch, setDoSearch] = useState<boolean>(false);

    useEffect(() => {
        if (authToken && Object.keys(issueData).length === 0 || doSearch) {
            const dp = new IssuesDataProvider();
            const headers = { "Auth-token": authToken };

            let params: Params = {};
            if (searchString && searchString !== '') {
                params.fulltext = searchString;
            }

            dp.getData(params, headers).then((res) => {
                if (res && res.status === "OK") {

                    if(doSearch){
                        setDoSearch(false);
                    }

                    const issuesArray = Object.values(res.data).flat() as Issue[];
                    const grouped = issuesArray.reduce<Record<string, Issue[]>>((acc, issue) => {
                        const year = issue.year;
                        if (!acc[year]) acc[year] = [];
                        acc[year].push(issue);
                        return acc;
                    }, {});
                    setIssueData(grouped);
                }
            });
        }
    }, [authToken, issueData, doSearch]);

    const sortedYears = Object.keys(issueData).sort((a, b) => Number(b) - Number(a));

    const bgColor = useColorModeValue('#f9f9f9', '#1a202c');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        let fieldValue = value;
        setSearchString(value);
    };


    const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setDoSearch(true);
    };


    return (
        <>
            {Object.keys(issueData).length > 0 && (
                <Box bg={bgColor}>
                    <Header />
                    <Container maxW={'8xl'}>
                        <chakra.h1 textAlign={'center'} fontSize={'4xl'} py={10} fontWeight={'bold'}>
                            Archiv vydání
                        </chakra.h1>
                        <JournalTabsInner sortedYears={sortedYears} issueData={issueData}/>
                    </Container>
                    <Footer />
                </Box>
            )}
        </>
    );
}


type JournalTabsInnerProps = {
    sortedYears: string[];
    issueData: any;
};


export const JournalTabsInner: React.FC<JournalTabsInnerProps> = ({ sortedYears, issueData}) => {
    const { isLoggedIn} = useEvoContext();

    const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>({});

    const toggleRow = (id: string) => {
        setExpandedRows((prevState) => ({
            ...prevState,
            [id]: !prevState[id],
        }));
    };

    return (
        <>
            <Tabs variant="soft-rounded" colorScheme="pink">
                <TabList
                    overflowX="auto"
                    display="flex"
                    justifyContent={{ base: "start", md: "center" }}
                    px={2}
                    css={{
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollSnapType: 'x mandatory',
                        '& > *': {
                            scrollSnapAlign: 'start',
                        },
                    }}
                >
                    {sortedYears.map((year) => (
                        <Tab key={year} whiteSpace="nowrap" p={{ base: 2, sm: 3 }}>
                            {year}
                        </Tab>
                    ))}
                </TabList>
                <TabPanels>
                    {sortedYears.map((year) => (
                        <TabPanel key={year}>
                            {issueData[year].some((issue : any) => issue.fulltext) ? (
                                <SimpleGrid columns={1} spacing={4} p={4}>
                                    {issueData[year].map((issue: any) => {

                                        const isExpanded = expandedRows[issue.id_issue] || false;

                                        return (
                                            <Box
                                                key={issue.id_issue}
                                                display="flex"
                                                flexDirection={{ base: "column", md: "row" }}
                                                alignItems={{ base: "center", md: "start" }}
                                                justifyContent="space-between"
                                                gap={4}
                                                width="100%"
                                            >
                                                {issue.fulltext && (
                                                    <Box
                                                        flexDirection="column"
                                                        alignItems={{ base: "center", md: "start" }}
                                                        textAlign={{ base: "center", md: "left" }}
                                                        width={{ base: "90%", md: "auto" }}
                                                    >
                                                        <chakra.h3
                                                            fontSize={{ base: "xl", md: "2xl" }}
                                                            pb={2}
                                                            fontWeight="bold"
                                                        >
                                                            {issue.title}
                                                        </chakra.h3>
                                                        <Box flex="1">
                                                            {Array.isArray(issue.fulltext) &&
                                                            issue.fulltext.slice(0, isExpanded ? issue.fulltext.length : 5).map((item: any, index: any) => (
                                                                    <Box
                                                                        key={index}
                                                                        fontSize={{ base: "md", md: "md" }}
                                                                        mb={2}
                                                                    >
                                                                        {item.page && (
                                                                            <ReadIssueLink
                                                                                textDecoration={"underline"}
                                                                                privateIssue={isLoggedIn}
                                                                                issue_id={issue.id_issue}
                                                                                page={item.page}
                                                                                label={
                                                                                    <div
                                                                                        dangerouslySetInnerHTML={{
                                                                                            __html:
                                                                                                "Stránka " +
                                                                                                item.page +
                                                                                                ": " +
                                                                                                item.fulltext,
                                                                                        }}
                                                                                    />
                                                                                }
                                                                            />
                                                                        )}
                                                                    </Box>
                                                                ))}

                                                            {issue.fulltext.length > 5 && (
                                                                <Box textAlign="left" mt={2}>
                                                                    <chakra.button
                                                                        onClick={() => toggleRow(issue.id_issue)}
                                                                        fontSize="sm"
                                                                        color="blue.500"
                                                                        textDecoration="underline"
                                                                        _hover={{ color: "blue.700" }}
                                                                    >
                                                                        {isExpanded
                                                                            ? "- Skrýt další stránky"
                                                                            : "+ Další nalezené stránky"}
                                                                    </chakra.button>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                )}

                                                <Box
                                                    flexShrink={0}
                                                    borderRadius="md"
                                                    overflow="hidden"
                                                    boxShadow="md"
                                                    maxWidth={{ base: "50%", md: "175px" }}
                                                    height="auto"
                                                >
                                                    <ReadIssueWithImageLink
                                                        privateIssue={isLoggedIn}
                                                        issue_id={issue.id_issue}
                                                        imageSrc={issue.image.image_url}
                                                        borderRadius="lg"
                                                        style={{
                                                            maxWidth: "100%",
                                                            height: "auto",
                                                            objectFit: "cover",
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </SimpleGrid>
                            ) : (
                                <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing={4} p={4}>
                                    {issueData[year].map((issue : any) => (
                                        <Box
                                            key={issue.id_issue}
                                            position="relative"
                                            borderRadius="md"
                                            overflow="hidden"
                                            boxShadow="md"
                                        >
                                            <ReadIssueWithImageLink
                                                privateIssue={isLoggedIn}
                                                issue_id={issue.id_issue}
                                                imageSrc={issue.image.image_url}
                                                borderRadius="lg"
                                                style={{
                                                    maxWidth: "100%",
                                                    height: "auto",
                                                    objectFit: "cover",
                                                }}
                                            />
                                        </Box>
                                    ))}
                                </SimpleGrid>
                            )}
                        </TabPanel>
                    ))}
                </TabPanels>
            </Tabs>
        </>
    );
};
